import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useRequestContext } from "../hooks/useRequest";
import { listingPrefill } from "../helpers/request";
import { ProductSearchForm } from "../../forms";
import { ProductPreview } from "../../product/components";
// Production V2 catalog cards: ProductList renders ProductListItem
// rows in a Bootstrap `row` / `col-md-3 col-6` grid that picks up the
// .listing.vertical styles scoped under body.retail-circle
// .find-product-product (rc-request.scss line ~956). Reusing this here
// means catalog UI on v2 stays pixel-identical to production.
import ProductList from "../../components/product/List";
import { getFileUrl } from "../../core/config/api.js";
import { getBaseCurrency, symbolFor } from "../../core/currency";
import { ProductHelper } from "../../helpers";
import { BrandChips } from "../../listingFlows/FlowKit.jsx";
import { Badge, Button, Segmented } from "../../components/ui";

// Import the listing form stylesheet so rc-lp__plist-* cards look identical.
import "../../forms/rc-listing-form.scss";

const LIMIT = 10;

const IImage = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="3" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.3"/>
    <circle cx="5.5" cy="6.5" r="1" fill="currentColor"/>
    <path d="M2 10.5L5 7.5L7.5 10L10 8L14 11.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// ── Active-listing picker ───────────────────────────────────────────
// Lists active listings the rep can use as a SOURCE for a main order
// Request. On select, the listing's product fills the slot and the
// CONFIRM REQUEST form is pre-filled with year / condition / price /
// box from the listing — so the rep doesn't retype data we already
// have in hand. Listing-as-source only applies to MAIN order slots;
// PX sells use a different chooser (Catalog / Existing sell / Paid
// cart) because reusing an active listing as a trade-in doesn't map
// to a real client lifecycle.
const RCListingList = ({ query, onSelect }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .post("/api/listings/search", {
        // Backend's filteringValidationRules requires `list` —
        // 'default' is the standard "everything visible to me" mode.
        list: "default",
        query: query || "",
        filters: { status: ["active"] },
        paging: { offset: 0, limit: LIMIT },
        sort: { field: "created_at", direction: "desc" },
      })
      .then((res) => setItems(res?.data?.items ?? []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [query]);

  if (loading) {
    return (
      <div className="rc-lp__plist-loading">
        <span className="rc-lp__plist-dot" />
        <span className="rc-lp__plist-dot" />
        <span className="rc-lp__plist-dot" />
      </div>
    );
  }

  if (!loading && items.length === 0) {
    return <div className="rc-lp__plist-empty">No active listings match.</div>;
  }

  // Canonical Listing Product Card (.pcard) — see memory
  // listing_product_card_design.md.
  return (
    <div className="pcard-grid">
      {items.map((listing) => {
        const product = listing.product || {};
        const imageUrl = product.images?.[0]?.id
          ? getFileUrl(product.images[0].id)
          : null;
        const metaParts = [product.reference_number, product.brand?.name].filter(Boolean);
        const subParts = [
          listing.year,
          listing.condition,
          listing.price ? `${symbolFor(listing.currency ?? getBaseCurrency())}${(listing.price / 100).toLocaleString()}` : null,
        ].filter(Boolean);
        return (
          <div key={listing.id} className="pcard">
            <div className="pcard__meta">
              {metaParts.map((p, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="pcard__bullet">•</span>}
                  <span>{p}</span>
                </React.Fragment>
              ))}
            </div>
            <div className="pcard__name">{product.model?.name || ""}</div>
            {subParts.length > 0 && (
              <div className="pcard__sub">{subParts.join(" / ")}</div>
            )}
            <div className="pcard__img">
              {imageUrl
                ? <img src={imageUrl} alt={product.model?.name || ""} />
                : <div className="pcard__noimg"><IImage /></div>
              }
            </div>
            <div className="pcard__foot">
              <Button variant="secondary" fullWidth data-btn="listing-source-confirm" onClick={() => onSelect(listing)}>
                Select
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Existing sell requests picker (PX-slot source) ───────────────────
// Lists the client's open sell Requests. The rep picks one and the
// PX sell slot reuses it instead of creating a new sell Request from
// scratch — e.g. "client already has a pending sell for that watch,
// roll it into the part-exchange of this order".
const RCExistingSellList = ({ clientId, query, onSelect }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientId) {
      setItems([]);
      return;
    }
    setLoading(true);
    // POST /api/user/{user}/requests/sell — server enforces
    // filters.status as a single string (validateRequestsFilters).
    // We don't want any status filter here per Alex's UX: every
    // sell request the client has (regardless of status) should be
    // pickable as a PX source.
    axios
      .post(`/api/user/${clientId}/requests/sell`, {
        filters: {
          ...(query ? { search: query } : {}),
        },
        paging: { offset: 0, limit: LIMIT },
        sort: { field: "created_at", direction: "desc" },
      })
      .then((res) => setItems(res?.data?.items ?? []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [clientId, query]);

  if (loading) {
    return (
      <div className="rc-lp__plist-loading">
        <span className="rc-lp__plist-dot" />
        <span className="rc-lp__plist-dot" />
        <span className="rc-lp__plist-dot" />
      </div>
    );
  }

  if (!loading && items.length === 0) {
    return (
      <div className="rc-lp__plist-empty">
        No open sell requests for this client.
      </div>
    );
  }

  return (
    <div className="pcard-grid">
      {items.map((req) => {
        const product = req.product || {};
        const imageUrl = product.images?.[0]?.id
          ? getFileUrl(product.images[0].id)
          : null;
        const metaParts = [product.reference_number, product.brand?.name].filter(Boolean);
        const subParts = [
          req.year,
          req.status,
          req.price_expectation
            // Price expectation renders in the sell REQUEST's own currency,
            // falling back to the store base.
            ? `${symbolFor(req.currency ?? getBaseCurrency())}${(req.price_expectation / 100).toLocaleString()}`
            : null,
        ].filter(Boolean);
        return (
          <div key={req.id} className="pcard">
            <div className="pcard__meta">
              {metaParts.map((p, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="pcard__bullet">•</span>}
                  <span>{p}</span>
                </React.Fragment>
              ))}
            </div>
            <div className="pcard__name">{product.model?.name || ""}</div>
            {subParts.length > 0 && (
              <div className="pcard__sub">{subParts.join(" / ")}</div>
            )}
            <div className="pcard__img">
              {imageUrl
                ? <img src={imageUrl} alt={product.model?.name || ""} />
                : <div className="pcard__noimg"><IImage /></div>
              }
            </div>
            <div className="pcard__foot">
              <Button variant="secondary" fullWidth data-btn="sellreq-select-confirm" onClick={() => onSelect(req)}>
                Select
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Paid cart-order items picker (PX-slot source) ────────────────────
// Lists OrderItems from the client's previously PAID orders so the
// rep can "trade back" something the client already bought from us.
const RCPaidCartList = ({ clientId, onSelect }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientId) {
      setItems([]);
      return;
    }
    setLoading(true);
    axios
      .get(`/api/users/${clientId}/paid-order-items`)
      .then((res) => setItems(res?.data?.items ?? []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [clientId]);

  if (loading) {
    return (
      <div className="rc-lp__plist-loading">
        <span className="rc-lp__plist-dot" />
        <span className="rc-lp__plist-dot" />
        <span className="rc-lp__plist-dot" />
      </div>
    );
  }

  if (!loading && items.length === 0) {
    return (
      <div className="rc-lp__plist-empty">No paid orders for this client.</div>
    );
  }

  return (
    <div className="pcard-grid">
      {items.map((item) => {
        const listing = item.listing || {};
        const product = listing.product || {};
        const imageUrl = product.images?.[0]?.id
          ? getFileUrl(product.images[0].id)
          : null;
        const orderDate = item.order?.created_at
          ? new Date(item.order.created_at).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : null;
        const metaParts = [product.reference_number, product.brand?.name].filter(Boolean);
        const subParts = [
          listing.year,
          listing.condition,
          // Order items don't carry a currency; the item's listing currency
          // is the order-price denomination (same rule as Payments.jsx).
          item.price ? `paid ${symbolFor(listing.currency ?? getBaseCurrency())}${(item.price / 100).toLocaleString()}` : null,
          orderDate,
        ].filter(Boolean);
        return (
          <div key={item.id} className="pcard">
            <div className="pcard__meta">
              {metaParts.map((p, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="pcard__bullet">•</span>}
                  <span>{p}</span>
                </React.Fragment>
              ))}
            </div>
            <div className="pcard__name">{product.model?.name || ""}</div>
            {subParts.length > 0 && (
              <div className="pcard__sub">{subParts.join(" / ")}</div>
            )}
            <div className="pcard__img">
              {imageUrl
                ? <img src={imageUrl} alt={product.model?.name || ""} />
                : <div className="pcard__noimg"><IImage /></div>
              }
            </div>
            <div className="pcard__foot">
              <Button variant="secondary" fullWidth data-btn="purchase-select-confirm" onClick={() => onSelect(item)}>
                Select
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export function V2SearchProduct({
  onAdvance,
  pxSellIdx = null,
  mainSlotIdx = null,
  // Tab this slot should open on, set by the button that created the slot
  // ("Add Listing" → 'listing', "Source Product" → 'catalog'). Overrides the
  // flow variant's own default; both lose to a real backfill pointer.
  defaultSource = null,
} = {}) {
  const {
    getContext,
    onProductSelected,
    client,
    setPxProducts,
    setPxRequests,
    flowVariant,
    listing: arrivedListing,
  } = useRequestContext();
  // Hard concurrency guard for the SELECT buttons. onProductSelected creates
  // the draft Request, and the request.id it produces isn't visible to a
  // second click's closure until React re-renders — so rapid multi-clicks
  // would each take the "no id yet" path and POST a fresh duplicate draft
  // (client_id defaulting to the logged-in admin). This ref makes the first
  // SELECT exclusive: further clicks are ignored until it resolves, by which
  // point either the step has advanced or request.id is set (→ PUT, no dup).
  const selectingRef = useRef(false);
  // Honor pxSellIdx so the dynamic SELECT SELL REQUEST (m/n) sections
  // read the px sell-request's product, not the main request's.
  // Honor mainSlotIdx so additional MAIN slots (2nd / 3rd products of
  // a multi-main deal) read from their own additionalMain* state.
  const step = {
    id:
      pxSellIdx != null
        ? `v2-Select Sell Request ${pxSellIdx}`
        : mainSlotIdx != null && mainSlotIdx > 0
          ? `v2-Add Product ${mainSlotIdx}`
          : "v2-Search Product",
    open: true,
    pxSellIdx,
    mainSlotIdx,
  };
  const ctx = getContext(0, step);
  const product = ctx.product;
  const slotRequest = ctx.request;
  const isSellFlow = ctx.isSell;
  const [filters, setFilters] = useState(null);

  // Auto-submit the search form on mount when the slot already has a linked
  // product. Without this, ProductList is gated behind `filters` being set
  // (only happens when the rep clicks Search or types in Refine), so opening
  // an existing request — e.g. /request/sell/:id — shows just the dropdowns
  // with no card, even though there's a product on the request. Mirroring
  // the PX-order behaviour: dropdowns pre-fill, the matching product appears
  // as a single result card the rep can SELECT (re-confirm) or replace.
  // Use product.id (not the whole object) as the dep so we only auto-search
  // when the linked product actually changes, not on every re-render.
  useEffect(() => {
    if (product?.id) {
      setFilters(ProductHelper.getInitialValues(product));
    }
  }, [product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Backfilled listing for the SEARCH section ─────────────────────────────
  // If this slot's Request has a direct linked_listing_id (from picking
  // "From active listing") OR has a last_quote with a listing_id (created
  // via SELECT QUOTE flow), surface that listing as the slot's source so
  // re-opening the request shows the existing relationship without the
  // rep having to dig through CONNECT LISTING / SELECT QUOTE history.
  // This honours the (request → quote → listing) chain that the old
  // CONNECT_LISTING section used to expose.
  // Active-listing backfill (main slots, and PX slots that picked
  // from Active Listings).
  // `arrivedListing` covers entering the flow FROM a listing (Record Sale on a
  // listing card pushes it through route state, which useRequest keeps as
  // `listing`). There is no persisted request yet at that point, so neither
  // pointer below exists — without this the rep would land on Active Listings
  // with their own listing unhighlighted and have to find it again. Primary
  // main slot only: an additional slot picks its own product.
  const isPrimaryMainForArrival = pxSellIdx == null && (mainSlotIdx ?? 0) === 0;
  const backfilledListingId =
    slotRequest?.linked_listing_id ??
    slotRequest?.last_quote?.listing_id ??
    (isPrimaryMainForArrival ? (arrivedListing?.id ?? null) : null);
  const [backfilledListing, setBackfilledListing] = useState(null);
  useEffect(() => {
    if (!backfilledListingId) {
      setBackfilledListing(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`/api/listings/${backfilledListingId}`)
      .then(({ data }) => {
        if (!cancelled) setBackfilledListing(data);
      })
      .catch(() => {
        if (!cancelled) setBackfilledListing(null);
      });
    return () => {
      cancelled = true;
    };
  }, [backfilledListingId]);

  // PX slot source backfill — when reopening a deal, the saved sell
  // Request remembers WHICH source the rep originally picked from:
  //   - source_sell_request_id  → "Sell Requests" tab
  //   - source_order_item_id    → "Previous Purchases" tab
  // These pointers were stored at creation time but the SEARCH
  // section wasn't surfacing them on reopen, so the rep saw the
  // Database tab as active and couldn't tell where the slot came
  // from. Fetch the linked entity and display it as a "Linked" card
  // at the top of the matching tab.
  const backfilledSellReqId = slotRequest?.source_sell_request_id ?? null;
  const backfilledOrderItemId = slotRequest?.source_order_item_id ?? null;

  const [backfilledSellReq, setBackfilledSellReq] = useState(null);
  useEffect(() => {
    if (!backfilledSellReqId) {
      setBackfilledSellReq(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`/api/requests/${backfilledSellReqId}`)
      .then(({ data }) => {
        if (!cancelled) setBackfilledSellReq(data);
      })
      .catch(() => {
        if (!cancelled) setBackfilledSellReq(null);
      });
    return () => {
      cancelled = true;
    };
  }, [backfilledSellReqId]);

  const [backfilledOrderItem, setBackfilledOrderItem] = useState(null);
  useEffect(() => {
    if (!backfilledOrderItemId || !client?.id) {
      setBackfilledOrderItem(null);
      return;
    }
    let cancelled = false;
    // No single-item endpoint for OrderItems; reuse the paid-cart
    // listing endpoint and pluck the matching row. For a typical
    // client this list is small (max 50 most-recent paid items).
    axios
      .get(`/api/users/${client.id}/paid-order-items`)
      .then(({ data }) => {
        if (cancelled) return;
        const match = (data?.items ?? []).find(
          (it) => String(it.id) === String(backfilledOrderItemId)
        );
        setBackfilledOrderItem(match || null);
      })
      .catch(() => {
        if (!cancelled) setBackfilledOrderItem(null);
      });
    return () => {
      cancelled = true;
    };
  }, [backfilledOrderItemId, client?.id]);

  // Source for the slot.
  // Main order slot:        'catalog' | 'listing'
  // Independent sell slot:  'catalog' | 'paid_cart'
  // PX sell slot:           'catalog' | 'existing_sell' | 'paid_cart'
  // Default is 'catalog' for all — preserves today's behaviour. If a
  // backfill pointer exists (linked_listing_id, source_sell_request_id,
  // or source_order_item_id), open the matching tab so the rep sees
  // the source they originally picked from.
  //
  // The `listing` source only renders for main order slots (see the
  // `{isMainSlot && source === "listing"}` block below). On the independent
  // Sell flow, `backfilledListingId` is often populated via the request's
  // `last_quote.listing_id`, but Sell has no Listings tab — selecting it
  // as the initial source leaves the SEARCH PRODUCT section empty because
  // no block matches. Gate each backfill source on the slot type it's
  // actually rendered for; fall back to `catalog`.
  const _isPxSlotForSource = pxSellIdx != null;
  const _isIndependentSellForSource = !_isPxSlotForSource && isSellFlow;
  const _isMainSlotForSource = !_isPxSlotForSource && !_isIndependentSellForSource;

  // Purpose-built flows open on a different tab than `catalog`: Record a Sale
  // starts on Active Listings, the enquiry flows on Database. A per-slot
  // preference (from the button that created the slot) beats the flow's, and a
  // real backfill pointer beats both — reopening a saved deal must always show
  // the source the rep actually picked from.
  const preferredSource = defaultSource ?? flowVariant?.searchDefault ?? null;
  // Tabs this variant opts into beyond the ones its slot kind normally shows.
  const showsExtraTab = (tab) => !!flowVariant?.extraTabs?.includes(tab);
  const preferredIsRenderable =
    preferredSource === "catalog" ||
    (preferredSource === "listing" && _isMainSlotForSource) ||
    (preferredSource === "existing_sell" && _isPxSlotForSource) ||
    (preferredSource === "paid_cart" &&
      (_isPxSlotForSource || _isIndependentSellForSource));

  const initialSource =
    _isMainSlotForSource && backfilledListingId
      ? "listing"
      : _isPxSlotForSource && backfilledSellReqId
        ? "existing_sell"
        : (_isPxSlotForSource || _isIndependentSellForSource) &&
            backfilledOrderItemId
          ? "paid_cart"
          : preferredIsRenderable
            ? preferredSource
            : "catalog";
  const [source, setSource] = useState(initialSource);
  const [listingQuery, setListingQuery] = useState("");
  const [existingSellQuery, setExistingSellQuery] = useState("");
  const [existingSellBrand, setExistingSellBrand] = useState(null);
  // Chip + free text combine into one AND search term for the picker.
  const existingSellTerm = [existingSellBrand, existingSellQuery]
    .filter(Boolean)
    .join(" ")
    .trim();

  // Why no axios.get(/api/products/:id) here:
  // The picker rows already carry the full product object (the search
  // endpoints embed brand/model/images), so the extra round trip the
  // previous handlers did added ~200-500ms of wait without surfacing
  // any field that wasn't already loaded. onProductSelected itself
  // does POST/PUT /api/requests which is the unavoidable persistence
  // hop — that's the floor on advance latency.
  //
  // onAdvance fires AFTER onProductSelected resolves: CONFIRM REQUEST
  // reads from `request` state set by onProductSelected, so advancing
  // earlier would render an empty form.
  const handleSelectProduct = async (p) => {
    if (!p?.id || selectingRef.current) return;
    selectingRef.current = true;
    try {
      // The two sources are mutually exclusive for a slot, so picking from
      // DATABASE must clear any Active-Listings pointer left on this request.
      // Without it a slot switched Listings -> Database keeps linked_listing_id
      // and stays treated as listing-sourced: no SELECT ORDER QUOTE section, for a
      // deal that now needs one. `RequestService::update` mass-assigns only the
      // keys it receives (it explicitly nulls year/budgets and nothing else), so
      // the null has to be sent deliberately.
      await onProductSelected(step, 0, p, undefined, {
        prefill: { linked_listing_id: null },
      });
      onAdvance?.();
    } finally {
      selectingRef.current = false;
    }
  };

  // Attach a listing to this slot: fills the product AND persists the fields
  // we already hold (year / condition / box / price) plus the
  // `linked_listing_id` pointer. That pointer is load-bearing well beyond the
  // prefill — PAYMENTS keys its visibility off it, and CONFIRM's "Finalise"
  // resolves its nav target through it — so a slot that has a listing must
  // always carry it.
  const linkListing = async (listing, { advance = true } = {}) => {
    if (!listing?.product_id || selectingRef.current) return;
    selectingRef.current = true;
    const prefill = listingPrefill(listing);
    try {
      await onProductSelected(
        step,
        0,
        listing.product || { id: listing.product_id },
        undefined,
        { prefill }
      );
      if (advance) onAdvance?.();
    } finally {
      selectingRef.current = false;
    }
  };

  const handleSelectListing = (listing) => linkListing(listing);

  // Clear the currently-linked Sell on this PX-sell slot. We deliberately
  // don't touch the existing Sell Request server-side — it stays a valid
  // standalone Sell — we just remove it from this PX bundle's slot so the
  // rep can pick a different one. On Finalise the un-linked Sell won't be
  // included in the PxRequest's `sell_requests` payload, and its
  // px_request_id stays as it was (null before the bundle was POSTed; the
  // backend handles "drop from bundle" on a subsequent PUT with the new
  // sell_requests list).
  const handleUnlinkSellSlot = () => {
    if (pxSellIdx == null) return;
    setPxRequests((arr) => {
      const next = [...(arr || [])];
      next[pxSellIdx] = null;
      return next;
    });
    setPxProducts((arr) => {
      const next = [...(arr || [])];
      next[pxSellIdx] = null;
      return next;
    });
  };

  const handleSelectExistingSell = async (sellReq) => {
    if (!sellReq?.product_id || selectingRef.current) return;
    selectingRef.current = true;
    const prefill = {
      year: sellReq.year || undefined,
      condition: sellReq.condition || undefined,
      delivery_set: sellReq.delivery_set || undefined,
      price_expectation: sellReq.price_expectation || undefined,
      source_sell_request_id: sellReq.id,
    };
    try {
      await onProductSelected(
        step,
        0,
        sellReq.product || { id: sellReq.product_id },
        undefined,
        { prefill }
      );
      onAdvance?.();
    } finally {
      selectingRef.current = false;
    }
  };

  const handleSelectPaidItem = async (orderItem) => {
    const listing = orderItem?.listing || {};
    const productId = listing.product_id;
    if (!productId || selectingRef.current) return;
    selectingRef.current = true;
    const prefill = {
      year: listing.year || undefined,
      condition: listing.condition ? [listing.condition] : undefined,
      delivery_set: listing.box || undefined,
      price_expectation: orderItem.price || listing.price || undefined,
      source_order_item_id: orderItem.id,
    };
    try {
      await onProductSelected(
        step,
        0,
        listing.product || { id: productId },
        undefined,
        { prefill }
      );
      onAdvance?.();
    } finally {
      selectingRef.current = false;
    }
  };

  const isPxSlot = pxSellIdx != null;
  // Independent SELL request flow — top-level /request/sell/... or a
  // /request/sell/:id reopen. Distinct from a PX sell slot (which
  // sits inside a px-order combination) because the rep can reuse
  // a paid cart item but NOT an existing sell request (that's the
  // very thing they're creating).
  const isIndependentSell = !isPxSlot && isSellFlow;
  // Main order slot — pxSellIdx unset AND not a sell flow.
  const isMainSlot = !isPxSlot && !isIndependentSell;

  // Linear-undo guard: once a listing is linked to the slot's quote
  // (order flow only), block product swap from this screen. The user
  // must remove the listing from the Listings section first. Prevents
  // a slip where changing the product silently invalidates the linked
  // listing's product_id references.
  const slotQuoteListingId =
    slotRequest?.last_quote?.listing_id ??
    slotRequest?.linked_listing_id ??
    null;
  const blockSwap =
    isMainSlot &&
    slotRequest?.request_type === "order" &&
    !!slotQuoteListingId;
  if (blockSwap) {
    return (
      <div className="rc-request-v2__search-blocked">
        <div className="rc-request-v2__search-blocked-title">
          Product locked
        </div>
        <div className="rc-request-v2__search-blocked-body">
          A listing is connected to this order. Remove the listing from the{" "}
          <strong>Listings</strong> section before changing the product.
        </div>
      </div>
    );
  }

  return (
    <div
      // add-listing-form + find-product-product replicate the exact
      // wrapper that the original SearchProduct.jsx uses in V2 mode
      // (line 53). This brings in:
      //  - rc-request.scss .find-product-product { form inputs, select
      //    controls, 2-col field layout, action button styles, ... }
      //  - rc-request.scss .find-product-product .listing.vertical
      //    { production-style product card grid }
      // So catalog + every picker source renders pixel-identically to
      // the production V2 SEARCH PRODUCT section.
      className="rc-lp__section rc-lp__section--product add-listing-form find-product-product"
      style={{ border: "none", borderRadius: 0 }}
    >
      <div className="rc-lp__section-body rc-lp__section-body--product">
        {/* "Currently selected" banner removed per UX feedback — the
            slot's currently-picked product shows within the picker
            tabs themselves (e.g. the "Linked" highlight inside the
            FROM ACTIVE LISTING tab). No need for a separate header. */}

        {/*
          Previous purchases is hidden on the independent sell flow's primary
          SEARCH PRODUCT section (/request/sell): that journey is "client
          wants to sell X to us", so Database is the only valid source. It
          always shows on PX sell slots ("trade in something they already paid
          us for"), and the Sell Enquiry variant opts back into it on its
          primary slot via extraTabs — scoped to that variant so
          /request/sell is unchanged.
        */}
        <Segmented
          label="Product source"
          fullWidth
          className="rc-lp__source-toggle"
          value={source}
          onChange={setSource}
          options={[
            { value: "catalog", label: "Database" },
            isMainSlot && { value: "listing", label: "Active listings" },
            isPxSlot && { value: "existing_sell", label: "Sell requests" },
            (isPxSlot || showsExtraTab("paid_cart")) && { value: "paid_cart", label: "Previous purchases" },
          ].filter(Boolean)}
        />

        {source === "catalog" && (
          // Production V2 catalog rendering — ProductSearchForm
          // (2-col field layout via .find-product-product scope) +
          // ProductList (col-md-3 col-6 .listing.vertical card grid)
          // + ProductPreview. Identical to what the original
          // SearchProduct.jsx renders when isV2 is true.
          <>
            <ProductSearchForm
              product={product}
              submitAction={(f) => setFilters(f)}
              filters={filters}
            />
            {filters && (
              <ProductList
                filters={filters}
                query={filters?.query}
                onSelectProduct={(p) => handleSelectProduct(p)}
                requestType={isSellFlow ? "sell" : "order"}
                // The V2 page's scroll lives on `.rc-request-v2__main` (id set
                // in RequestTemplateV2). Without this, react-infinite-scroll
                // listens on window scroll and never fires the next page.
                scrollableTarget="rc-request-v2-scroll"
              />
            )}
            <ProductPreview onSelect={handleSelectProduct} />
          </>
        )}

        {isMainSlot && source === "listing" && (
          <>
            {backfilledListing && (() => {
              // Canonical .pcard with the --linked (gold) variant.
              const bp = backfilledListing.product || {};
              const bImg = bp.images?.[0]?.id ? getFileUrl(bp.images[0].id) : null;
              const bMeta = [bp.reference_number, bp.brand?.name].filter(Boolean);
              const bSub = [
                backfilledListing.year,
                backfilledListing.condition,
                backfilledListing.price
                  ? `${symbolFor(backfilledListing.currency ?? getBaseCurrency())}${(backfilledListing.price / 100).toLocaleString()}`
                  : null,
              ].filter(Boolean);
              return (
                <div className="pcard-grid pcard-grid--single">
                  <div className="pcard pcard--linked">
                    <div className="pcard__meta">
                      {bMeta.map((p, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span className="pcard__bullet">•</span>}
                          <span>{p}</span>
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="pcard__name">{bp.model?.name || ""}</div>
                    {bSub.length > 0 && (
                      <div className="pcard__sub">{bSub.join(" / ")}</div>
                    )}
                    <div className="pcard__img">
                      {bImg
                        ? <img src={bImg} alt={bp.model?.name || ""} />
                        : <div className="pcard__noimg"><IImage /></div>
                      }
                    </div>
                    <div className="pcard__foot">
                      <Badge tone="warning">Linked</Badge>
                    </div>
                  </div>
                </div>
              );
            })()}
            <div className="rc-lp__listing-search">
              <input
                type="text"
                placeholder="Search active listings…"
                className="rc-lp__listing-search-input"
                value={listingQuery}
                onChange={(e) => setListingQuery(e.target.value)}
              />
            </div>
            <RCListingList query={listingQuery} onSelect={handleSelectListing} />
          </>
        )}

        {isPxSlot && source === "existing_sell" && (
          <>
            {(() => {
              // Show the currently-linked Sell as a `--linked` card so the
              // rep can see what was picked on reopen and unlink it if they
              // want to swap. Two sources for "currently linked":
              //   1. `backfilledSellReq` — the original existing Sell the
              //      slot was created from (only set when the slot's saved
              //      Request carries `source_sell_request_id`).
              //   2. `slotRequest` itself — when the rep picked from "Sell
              //      Requests" we now reuse the existing Sell directly as
              //      the slot's Request, so backfilledSellReq stays null
              //      but slotRequest carries the linked sell's data.
              // Prefer the slotRequest path (more current) and fall back to
              // backfilledSellReq for legacy slots that created a new draft
              // pointing at the source.
              const linkedSell = slotRequest?.id ? slotRequest : backfilledSellReq;
              if (!linkedSell) return null;
              const bp = linkedSell.product || {};
              const bImg = bp.images?.[0]?.id
                ? getFileUrl(bp.images[0].id)
                : null;
              const bMeta = [bp.reference_number, bp.brand?.name].filter(Boolean);
              const bSub = [
                linkedSell.year,
                linkedSell.status,
                linkedSell.price_expectation
                  // Price expectation in the sell request's own currency,
                  // falling back to the store base.
                  ? `${symbolFor(linkedSell.currency ?? getBaseCurrency())}${(linkedSell.price_expectation / 100).toLocaleString()}`
                  : null,
              ].filter(Boolean);
              return (
                <div className="pcard-grid pcard-grid--single">
                  <div className="pcard pcard--linked">
                    <div className="pcard__meta">
                      {bMeta.map((p, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span className="pcard__bullet">•</span>}
                          <span>{p}</span>
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="pcard__name">{bp.model?.name || ""}</div>
                    {bSub.length > 0 && (
                      <div className="pcard__sub">{bSub.join(" / ")}</div>
                    )}
                    <div className="pcard__img">
                      {bImg
                        ? <img src={bImg} alt={bp.model?.name || ""} />
                        : <div className="pcard__noimg"><IImage /></div>
                      }
                    </div>
                    <div className="pcard__foot">
                      <Badge tone="warning">Linked</Badge>
                      <Button variant="secondary" size="sm" data-btn="sellreq-link-delete" onClick={() => handleUnlinkSellSlot()}>
                        Unlink
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })()}
            <div className="rc-lp__listing-search">
              <input
                type="text"
                placeholder="Search by client, store, company, brand, model or reference…"
                className="rc-lp__listing-search-input"
                value={existingSellQuery}
                onChange={(e) => setExistingSellQuery(e.target.value)}
              />
            </div>
            <BrandChips active={existingSellBrand} onToggle={setExistingSellBrand} />
            <RCExistingSellList
              clientId={client?.id}
              query={existingSellTerm}
              onSelect={handleSelectExistingSell}
            />
          </>
        )}

        {/* Previous Purchases content only renders on PX sell slots
            (within a px-order combination). The standalone /request/sell
            primary SEARCH does not surface this tab — see the tab
            visibility block above for rationale. */}
        {(isPxSlot || showsExtraTab("paid_cart")) && source === "paid_cart" && (
          <>
            {(() => {
              // Mirror the existing_sell tab logic: prefer slotRequest as
              // the current "linked" indicator (it carries the freshly
              // picked paid-cart item's product + prefill data), and fall
              // back to backfilledOrderItem for the legacy pointer. With
              // either source, render the `--linked` card + Unlink button
              // so the rep can swap if needed.
              const fromBackfill = !!backfilledOrderItem;
              const linkedListing = fromBackfill
                ? backfilledOrderItem.listing || {}
                : slotRequest?.product
                  ? { product: slotRequest.product, ...slotRequest }
                  : null;
              if (!linkedListing) return null;
              const bp = linkedListing.product || {};
              const bImg = bp.images?.[0]?.id
                ? getFileUrl(bp.images[0].id)
                : null;
              const orderDate = fromBackfill && backfilledOrderItem.order?.created_at
                ? new Date(
                    backfilledOrderItem.order.created_at
                  ).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })
                : null;
              const bMeta = [bp.reference_number, bp.brand?.name].filter(Boolean);
              const bSub = [
                linkedListing.year,
                linkedListing.condition,
                fromBackfill && backfilledOrderItem.price
                  // Paid price is denominated in the item's listing
                  // currency (linkedListing = backfilledOrderItem.listing
                  // on this branch), store-base fallback.
                  ? `paid ${symbolFor(linkedListing.currency ?? getBaseCurrency())}${(backfilledOrderItem.price / 100).toLocaleString()}`
                  : slotRequest?.price_expectation
                    // Request currency, store-base fallback.
                    ? `${symbolFor(slotRequest.currency ?? getBaseCurrency())}${(slotRequest.price_expectation / 100).toLocaleString()}`
                    : null,
                orderDate,
              ].filter(Boolean);
              return (
                <div className="pcard-grid pcard-grid--single">
                  <div className="pcard pcard--linked">
                    <div className="pcard__meta">
                      {bMeta.map((p, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span className="pcard__bullet">•</span>}
                          <span>{p}</span>
                        </React.Fragment>
                      ))}
                    </div>
                    <div className="pcard__name">{bp.model?.name || ""}</div>
                    {bSub.length > 0 && (
                      <div className="pcard__sub">{bSub.join(" / ")}</div>
                    )}
                    <div className="pcard__img">
                      {bImg
                        ? <img src={bImg} alt={bp.model?.name || ""} />
                        : <div className="pcard__noimg"><IImage /></div>
                      }
                    </div>
                    <div className="pcard__foot">
                      <Badge tone="warning">Linked</Badge>
                      <Button variant="secondary" size="sm" data-btn="purchase-link-delete" onClick={() => handleUnlinkSellSlot()}>
                        Unlink
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })()}
            <RCPaidCartList
              clientId={client?.id}
              onSelect={handleSelectPaidItem}
            />
          </>
        )}
      </div>
    </div>
  );
}
